// Fuente única de verdad para el gating de MODOS por plan. Antes esto estaba
// disperso en `plan === 'gratis'` sueltos por InterfazV2; centralizarlo acá
// hace fácil auditar/cambiar qué modo necesita qué plan.
//
// Planes (company_profiles.plan): gratis · emprendimiento (Básico) ·
// negocio (Pro) · enterprise/enterprise1/2/3 (Ultra) · enterprise_plus (Empresa).

const PRO_OR_ULTRA = new Set([
  'negocio',
  'enterprise',
  'enterprise1',
  'enterprise2',
  'enterprise3',
  'enterprise_plus',
]);

// Solo planes Ultra (enterprise + niveles). El custom Empresa (enterprise_plus)
// se incluye porque comparte todas las capabilities de los Ultra.
const ULTRA = new Set([
  'enterprise',
  'enterprise1',
  'enterprise2',
  'enterprise3',
  'enterprise_plus',
]);

const norm = (plan?: string | null) => (plan ?? '').toString().trim().toLowerCase();

/** Pro y Ultra (negocio + enterprise*). Empresa (enterprise_plus) incluido. */
export function isProOrUltra(plan?: string | null): boolean {
  return PRO_OR_ULTRA.has(norm(plan));
}

/** Solo planes Ultra (enterprise/enterprise1/2/3 + el custom Empresa
 *  enterprise_plus). Excluye Pro (negocio). */
export function isUltra(plan?: string | null): boolean {
  return ULTRA.has(norm(plan));
}

/** Cualquier plan pago (distinto de gratis). */
export function isPaidPlan(plan?: string | null): boolean {
  const p = norm(plan);
  return p.length > 0 && p !== 'gratis';
}

export interface ModePlanLock {
  /** Identificador de la feature bloqueada (para telemetría / debugging). */
  feature: 'catalogo' | 'asistente' | 'editor-video' | 'video-influencer';
  title: string;
  message: string;
}

/** Nota corta del plan que requiere una feature bloqueada — la muestra el
 *  visor de ejemplos de modos (ModeExamplesModal) junto al CTA "Mejorar
 *  plan". Espeja las reglas de getModePlanLock. */
export function getPlanRequirementNote(
  feature: ModePlanLock['feature'],
): string {
  switch (feature) {
    case 'catalogo':
      return 'Función disponible desde el plan Básico.';
    case 'asistente':
    case 'video-influencer':
      return 'Función disponible para los planes Pro y Ultra.';
    case 'editor-video':
      return 'Función disponible únicamente con el plan Ultra.';
  }
}

/**
 * Devuelve el lock a mostrar si el `plan` NO alcanza para el modo `kind`, o
 * `null` si está permitido.
 *
 * Reglas:
 *   • catalogo-ia      → requiere plan pago (bloqueado en gratis; disponible desde Básico).
 *   • asistente        → requiere Pro o Ultra (bloqueado en gratis y Básico).
 *   • editor-video     → requiere Ultra (el Estudio de Video pro; bloqueado en el resto).
 *   • video-influencer → requiere Pro o Ultra (ADR-564).
 *
 * IMPORTANTE: si `plan` todavía no se conoce (null/''), NO bloqueamos — así no
 * le negamos el acceso a un usuario pago mientras su plan carga. El guard de
 * generación (que pide el plan fresco a Supabase) queda como backstop.
 */
export function getModePlanLock(
  kind: string | null | undefined,
  plan: string | null | undefined,
): ModePlanLock | null {
  if (!kind || !norm(plan)) return null;

  // Mismo lock para los dos modos de catálogo: ambos generan en volumen
  // (una imagen por vista × producto), así que comparten la regla de plan pago
  // y la misma `feature` para el copy y la telemetría.
  if (
    (kind === 'catalogo-ia' ||
      kind === 'catalogo-muebles' ||
      kind === 'infografia') &&
    !isPaidPlan(plan)
  ) {
    return {
      feature: 'catalogo',
      title: 'Catálogo IA es una función paga',
      message:
        'La generación de catálogos con IA no está disponible en la prueba gratis. Mejorá tu plan para desbloquearla.',
    };
  }

  if (kind === 'asistente' && !isProOrUltra(plan)) {
    return {
      feature: 'asistente',
      title: 'Crear desde cero es una función Pro',
      message:
        'Crear desde cero está disponible únicamente para los planes Pro y Ultra. Mejorá tu plan para usarlo.',
    };
  }

  if (kind === 'video-influencer' && !isProOrUltra(plan)) {
    return {
      feature: 'video-influencer',
      title: 'Video con influencer es una función Pro',
      message:
        'La generación de videos con influencer IA está disponible para los planes Pro y Ultra. Mejorá tu plan para usarla.',
    };
  }

  if (kind === 'editor-video' && !isUltra(plan)) {
    return {
      feature: 'editor-video',
      title: 'El Estudio de Video es una función Ultra',
      message:
        'El Estudio de Video con IA está disponible únicamente para el plan Ultra. Mejorá tu plan para desbloquearlo.',
    };
  }

  return null;
}
